import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, GraduationCap, Play, Clock, Users, Star, 
  Calendar, Video, BookOpen, Award, ChevronRight, Filter
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Sample live classes data
const LIVE_CLASSES = [
  {
    id: 1,
    title: "Method Acting Masterclass",
    instructor: "Priya Sharma",
    category: "Acting",
    duration: "2 hours",
    date: "Tomorrow, 4:00 PM",
    participants: 45,
    maxParticipants: 50,
    rating: 4.9,
    price: 499,
    image: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=400",
    live: true,
    tags: ["Beginner Friendly", "Certificate"]
  },
  {
    id: 2,
    title: "Voice Modulation for Actors",
    instructor: "Vikram Desai",
    category: "Voice",
    duration: "1.5 hours",
    date: "Jan 5, 6:00 PM",
    participants: 28,
    maxParticipants: 40,
    rating: 4.8,
    price: 349,
    image: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400",
    live: false,
    tags: ["All Levels"]
  },
  {
    id: 3,
    title: "Bollywood Dance Workshop",
    instructor: "Neha Kapoor",
    category: "Dance",
    duration: "2 hours",
    date: "Jan 6, 5:00 PM",
    participants: 62,
    maxParticipants: 100,
    rating: 4.7,
    price: 299,
    image: "https://images.unsplash.com/photo-1547153760-18fc86324498?w=400",
    live: false,
    tags: ["Intermediate", "Certificate"]
  },
  {
    id: 4,
    title: "Screen Presence & Camera Acting",
    instructor: "Arjun Mehta",
    category: "Acting",
    duration: "3 hours",
    date: "Jan 7, 3:00 PM",
    participants: 35,
    maxParticipants: 50,
    rating: 4.9,
    price: 699,
    image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400",
    live: false,
    tags: ["Advanced", "Certificate"]
  }
];

const RECORDED_COURSES = [
  {
    id: 1,
    title: "Complete Acting Foundation",
    instructor: "Multiple Trainers",
    lessons: 24,
    duration: "12 hours",
    enrolled: 1250,
    rating: 4.9,
    price: 1999,
    image: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=400",
    tags: ["Bestseller", "Certificate"]
  },
  {
    id: 2,
    title: "Audition Preparation Guide",
    instructor: "Industry Experts",
    lessons: 18,
    duration: "8 hours",
    enrolled: 890,
    rating: 4.8,
    price: 1499,
    image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400",
    tags: ["Popular"]
  }
];

const CATEGORIES = ["All", "Acting", "Voice", "Dance", "Modeling", "Music"];

export default function LiveSchool() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeTab, setActiveTab] = useState("live");

  const filteredClasses = selectedCategory === "All" 
    ? LIVE_CLASSES 
    : LIVE_CLASSES.filter(c => c.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/talent/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
                <div>
                  <span className="font-display text-lg font-bold text-gray-900">Live School</span>
                  <p className="text-xs text-gray-500">Learn from Industry Experts</p>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm">
              <Calendar className="w-4 h-4 mr-2" /> My Schedule
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-violet-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <Badge className="bg-white/20 text-white mb-3">New Classes Every Week</Badge>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">Master Your Craft</h1>
              <p className="text-white/80 text-sm md:text-base">
                Live interactive classes with top industry trainers. Learn acting, voice, dance & more.
              </p>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-3xl font-bold">50+</p>
                <p className="text-sm text-white/70">Live Classes</p>
              </div>
              <div>
                <p className="text-3xl font-bold">25+</p>
                <p className="text-sm text-white/70">Expert Trainers</p>
              </div>
              <div>
                <p className="text-3xl font-bold">5000+</p>
                <p className="text-sm text-white/70">Students</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="live" data-testid="tab-live">
              <Video className="w-4 h-4 mr-2" /> Live Classes
            </TabsTrigger>
            <TabsTrigger value="recorded" data-testid="tab-recorded">
              <BookOpen className="w-4 h-4 mr-2" /> Recorded Courses
            </TabsTrigger>
            <TabsTrigger value="my-courses" data-testid="tab-my-courses">
              <GraduationCap className="w-4 h-4 mr-2" /> My Learning
            </TabsTrigger>
          </TabsList>

          {/* Live Classes */}
          <TabsContent value="live" className="mt-6">
            {/* Category Filter */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
              {CATEGORIES.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? "bg-violet-600" : ""}
                >
                  {cat}
                </Button>
              ))}
            </div>

            {/* Class Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClasses.map(cls => (
                <Card key={cls.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" data-testid={`class-${cls.id}`}>
                  <div className="relative">
                    <img src={cls.image} alt={cls.title} className="w-full h-40 object-cover" />
                    {cls.live && (
                      <Badge className="absolute top-3 left-3 bg-red-500 text-white">
                        <span className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></span>
                        Live Now
                      </Badge>
                    )}
                    <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {cls.duration}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {cls.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{cls.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">by {cls.instructor}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {cls.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {cls.participants}/{cls.maxParticipants}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{cls.rating}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-violet-600">₹{cls.price}</span>
                        <Button size="sm" className="ml-2 bg-violet-600 hover:bg-violet-700">
                          {cls.live ? "Join Now" : "Book Seat"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Recorded Courses */}
          <TabsContent value="recorded" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4">
              {RECORDED_COURSES.map(course => (
                <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="flex">
                    <img src={course.image} alt={course.title} className="w-32 h-32 object-cover" />
                    <CardContent className="p-4 flex-1">
                      <div className="flex flex-wrap gap-1 mb-2">
                        {course.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{course.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">by {course.instructor}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                        <span>{course.lessons} lessons</span>
                        <span>{course.duration}</span>
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          {course.rating}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="font-bold text-violet-600">₹{course.price}</span>
                        <Button size="sm" variant="outline">
                          View Course <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* My Learning */}
          <TabsContent value="my-courses" className="mt-6">
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">No enrolled courses yet</h3>
              <p className="text-gray-500 text-sm mb-4">Browse our live classes and courses to start learning</p>
              <Button onClick={() => setActiveTab("live")} className="bg-violet-600 hover:bg-violet-700">
                Explore Classes
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Demo Notice */}
      <div className="bg-amber-50 border-t border-amber-200 px-4 py-2 text-center">
        <p className="text-xs text-amber-700">
          <Badge variant="outline" className="mr-2">Demo Mode</Badge>
          Live School is ready for Zoom/Google Meet integration for real classes.
        </p>
      </div>
    </div>
  );
}
